import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, parseApiError } from '../lib/api';
import { Card } from './Card';
import { DiscordMemberPicker } from './DiscordMemberPicker';
import { UserCog, CheckCircle2 } from 'lucide-react';

// ── Shared helpers (local copies — matches the idiom in AdminPanel.tsx /
// ClanWorkbench.tsx of each file owning its own tiny message renderers) ──────

// Same shortId idiom as OrgMembersPanel/OrgProposalsPanel/OrgClansPanel/
// CompetitionMembersPanel — never render a raw Discord snowflake.
function shortId(discordId: string): string {
  return `…${discordId.slice(-6)}`;
}

function ErrorMsg({ msg }: { msg: string }) {
  return msg ? <p className="mt-2 text-xs text-red-400">{msg}</p> : null;
}

function SuccessMsg({ msg }: { msg: string }) {
  return msg ? (
    <p className="mt-2 flex items-center gap-1.5 text-xs text-green-400">
      <CheckCircle2 className="h-3.5 w-3.5" /> {msg}
    </p>
  ) : null;
}

// ── Transfer Leadership ───────────────────────────────────────────────────────
//
// Needs the platform-wide clan list (not a competition-scoped one), so it
// fetches api.admin.clans() itself rather than taking clans from its host.

export function TransferLeadershipPanel() {
  const { data: clans } = useQuery({ queryKey: ['admin', 'clans'], queryFn: api.admin.clans });
  const clanList = clans ?? [];

  const [clanName, setClanName] = useState('');
  const [newLeaderId, setNewLeaderId] = useState('');
  const [newLeaderName, setNewLeaderName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const qc = useQueryClient();

  const mut = useMutation({
    mutationFn: () => api.admin.transferLeadership(clanName, newLeaderId.trim()),
    onSuccess: (data) => {
      setSuccess(`Done — ${data.clanName} is now led by ${newLeaderName || shortId(data.newLeaderDiscordId)}`);
      setNewLeaderId('');
      setNewLeaderName('');
      setError('');
      qc.invalidateQueries({ queryKey: ['admin', 'clans'] });
    },
    onError: (e: Error) => { setError(parseApiError(e).message); setSuccess(''); },
  });

  return (
    <Card>
      <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-ink-meta">
        <UserCog className="h-4 w-4 text-ink-secondary" /> Transfer Leadership
      </h2>
      <div className="space-y-3">
        <div>
          <label className="label">Clan</label>
          <select
            value={clanName}
            onChange={e => { setClanName(e.target.value); setNewLeaderId(''); setNewLeaderName(''); setError(''); setSuccess(''); }}
            className="input w-full"
          >
            <option value="">Select clan…</option>
            {clanList.map(c => (
              <option key={c.id} value={c.name}>
                {c.name} {c.leaderId === 'IMPORT' ? '(no leader set)' : `— ${c.leaderName ?? shortId(c.leaderId)}`}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">New Leader</label>
          <DiscordMemberPicker
            key={clanName}
            value={newLeaderId}
            selectedLabel={newLeaderName || undefined}
            onChange={(id, member) => {
              setNewLeaderId(id);
              setNewLeaderName(member?.displayName ?? '');
              setError('');
              setSuccess('');
            }}
            search={q => api.searchDiscordMembers(clanName, q)}
            disabled={!clanName}
            disabledHint="Select a clan first"
            placeholder="Type a name…"
          />
        </div>
        {error && <ErrorMsg msg={error} />}
        {success && <SuccessMsg msg={success} />}
        <button
          onClick={() => mut.mutate()}
          disabled={!clanName || !newLeaderId.trim() || mut.isPending}
          className="btn-primary w-full"
        >
          {mut.isPending ? 'Transferring…' : 'Transfer Leadership'}
        </button>
      </div>
    </Card>
  );
}
